import React, { ReactElement } from 'react';
import Button from '@/ui/components/shared/Button';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { useTranslations } from 'next-intl';

export interface DesktopAddToCartProps {
  price?: string;
  addToCart: () => void;
  disabled?: boolean;
  isAvailable: boolean;
  isBundleSelected?: boolean;
  onNotify: () => void;
  isUS: boolean;
  /**
   * Shown instead of "Add to cart", e.g. to unlock a Nuki Club only item. An element, not any
   * node, so a `condition && <X />` (false) cannot hide the button by accident.
   */
  purchaseActions?: ReactElement;
}

export function DesktopAddToCart({
  price,
  addToCart,
  disabled,
  isAvailable,
  isBundleSelected,
  onNotify,
  isUS,
  purchaseActions,
}: Readonly<DesktopAddToCartProps>) {
  const t = useTranslations('ProductChooser');

  return (
    <div className="sticky bottom-0 hidden w-full bg-white px-6 py-3 shadow-nuki-blured lg:block">
      {isAvailable ? (
        <div className="flex justify-between">
          <p className="text-md font-medium">{t('Subtotal')}</p>
          <p className="text-md font-bold">
            {price}
            {!isUS && (
              <span className="ml-1 text-sm font-normal text-neutral-50">{t('incl VAT')}</span>
            )}
          </p>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-3 text-red">
          <ExclamationTriangleIcon className="h-8 w-8" />
          <p className="text-base font-bold">
            {isBundleSelected
              ? t('The selected bundle is currently not available')
              : t('This product variant is currently not available')}
          </p>
        </div>
      )}
      {isAvailable ? (
        <div className="mt-3">
          {purchaseActions ?? (
            <Button
              className="w-full whitespace-nowrap"
              disabled={disabled}
              type="button"
              onClick={addToCart}
            >
              {t('Add to cart')}
            </Button>
          )}
        </div>
      ) : (
        <Button
          className="mt-3  w-full whitespace-nowrap py-2"
          disabled={disabled}
          type="button"
          onClick={onNotify}
        >
          {t('Notify me')}
        </Button>
      )}
    </div>
  );
}
